import { RouterModule, Routes } from '@angular/router';
import { MenuItem } from './modules/core/enums/menu.enum';
import { WorkflowToken } from './modules/core/enums/workflow-token.enum';
import { AuthenticatedGuard } from './modules/core/guards/authenticated.guard';
import { ForDeveloperGuard } from './modules/core/guards/for-developer.guard';
import { SessionLoginGuard } from './modules/core/guards/session-login.guard';
import { SetRegisterGuard } from './modules/core/guards/set-register.guard';
import { SelectivePreloadingStrategyService } from './modules/core/services/selective-preloading-strategy.service';
import { StartComponent } from './sections/start/start.component';
import * as defaultConfig from 'src/environments/config.json';

// Set routes
// For rights see https://redmine.werkdigital.net/projects/sir-cp4/wiki/Rechtemanagement
const appRoutes: Routes = [
	// Default for empty path (see default on last route)
	{
		path: '',
		component: StartComponent,
		canActivate: [SessionLoginGuard],
		pathMatch: 'full',
		data: {
			internal: false
		}
	},

	// Development docs
	{
		path: 'development',
		loadChildren: () => import('./sections/development/development.module').then((m) => m.SectionDevelopmentModule),
		canActivate: [SessionLoginGuard, ForDeveloperGuard],
		data: {
			internal: false
		}
	},

	// User authentication
	{
		path: 'user-auth',
		loadChildren: () => import('./sections/user-auth/user-auth.module').then((m) => m.SectionUserAuthModule),
		canActivate: [SessionLoginGuard],
		data: {
			internal: false
		}
	},

	// User login
	{
		path: 'login',
		loadChildren: () => import('./sections/login/login.module').then((m) => m.SectionLoginModule),
		canActivate: [SessionLoginGuard],
		data: {
			breadcrumb: MenuItem.LOGIN,
			breadcrumbHideLink: true,
			internal: false
		}
	},

	// Register for lab and dentist
	{
		path: 'register',
		loadChildren: () => import('./sections/register/register.module').then((m) => m.SectionRegisterModule),
		canActivate: [SessionLoginGuard, SetRegisterGuard],
		data: {
			internal: false
		}
	},

	// Dashboard
	{
		path: 'dashboard',
		loadChildren: () => import('./sections/dashboard/dashboard.module').then((m) => m.SectionDashboardModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			internal: true
		}
	},

	{
		path: 'user',
		loadChildren: () => import('./sections/user/user.module').then((m) => m.SectionUserModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.MY_USER,
			breadcrumbHideLink: true,
			guardAllowedRoles: [],
			guardAllowedWorkflows: [WorkflowToken.ACCOUNT_USER_SELF_ADMINISTRATION, WorkflowToken.ACCOUNT_USER_RESET_PASSWORD],
			internal: true
		}
	},
	{
		path: 'customer',
		loadChildren: () => import('./sections/customer/customer.module').then((m) => m.SectionCustomerModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.MY_COMPANY,
			breadcrumbHideLink: true,
			guardAllowedRoles: [],
			guardAllowedWorkflows: [WorkflowToken.ACCOUNT_USER_SELF_ADMINISTRATION, WorkflowToken.ACCOUNT_USER_RESET_PASSWORD],
			internal: true
		}
	},
	{
		path: 'partner',
		loadChildren: () => import('./sections/partner/partner.module').then((m) => m.SectionPartnerModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.PARTNER,
			breadcrumbHideLink: true,
			guardAllowedRoles: [],
			guardAllowedWorkflows: [WorkflowToken.ACCOUNT_USER_SELF_ADMINISTRATION, WorkflowToken.ACCOUNT_USER_RESET_PASSWORD],
			internal: true
		}
	},
	{
		path: 'orderlist',
		loadChildren: () => import('./sections/order-list/order-list.module').then((m) => m.SectionOrderListModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.ORDER_LIST,
			breadcrumbHideLink: true,
			guardAllowedRoles: [],
			guardAllowedWorkflows: [WorkflowToken.ACCOUNT_CUSTOMER_ORDER_LIST_SHOW],
			internal: true
		}
	},
	{
		path: 'users',
		loadChildren: () => import('./sections/users/users.module').then((m) => m.SectionUsersModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.USER_ADMINISTRATION,
			breadcrumbHideLink: true,
			guardAllowedRoles: [],
			guardAllowedWorkflows: [WorkflowToken.ACCOUNT_USER_ADD, WorkflowToken.ACCOUNT_DISPLAY_CUSTOMER_USER_MANAGEMENT],
			internal: true
		}
	},
	{
		path: 'connectinbox',
		loadChildren: () => import('./sections/connect-in-box/connect-in-box.module').then((m) => m.SectionConnectInBoxModule),
		canActivate: [SessionLoginGuard, AuthenticatedGuard],
		data: {
			breadcrumb: MenuItem.CONNECT_CASE_CENTER_INBOX,
			breadcrumbHideLink: true,
			internal: true
		}
	},
	{
		path: 'external',
		loadChildren: () => import('./sections/external/external.module').then((m) => m.SectionExternalModule),
		canActivate: [SessionLoginGuard],
		data: {
			internal: false,
			preload: true
		}
	},

	// default (see default for empty path on the first route)
	{
		path: '**',
		redirectTo: '/dashboard/welcome'
	}
];

// Export routing
export const AppRouting = RouterModule.forRoot(appRoutes, {
	// enable router logging
	enableTracing: defaultConfig.routeTracing,
	preloadingStrategy: SelectivePreloadingStrategyService,
	relativeLinkResolution: 'legacy'
});
